
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { FileText } from "lucide-react";
import ClustrLogo from "@/components/icons/ClustrLogo";
import Footer from "@/components/landing/Footer";
import { useMetadata } from "@/hooks/useMetadata";
import { APP_METADATA } from "@/constants/metadata";

const Releases = () => {
  const { title, description, image } = APP_METADATA.releases;
  useMetadata({ title, description, image });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="relative">
        <div className="w-full">
          <img 
            src="/lovable-uploads/74c71e1c-f446-4558-a10a-e69c985160f7.png" 
            alt="Clustr Network Visualization" 
            className="w-full object-contain"
          />
        </div>
        
        <div className="absolute top-4 left-0 w-full px-6">
          <div className="max-w-6xl mx-auto">
            <Link to="/">
              <ClustrLogo 
                width={180} 
                height={54} 
                src="/lovable-uploads/abf9b449-c10d-4d99-b03c-c106652be5bb.png"
              />
            </Link>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 w-full px-6 pb-6 bg-gradient-to-t from-white via-white/80 to-transparent">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-5">
              <FileText className="h-20 w-20 text-clustr-blue" />
              <h1 className="text-6xl font-bold text-clustr-text">Release Notes</h1>
            </div>
          </div>
        </div>
      </div>

      <main className="flex-grow">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="prose max-w-none">
            <h2 className="text-2xl font-semibold mt-8 mb-4">About This Experiment</h2>
            <p>
              Clustr is currently in an experimental phase where we're validating the core concept and features. 
              Your participation helps us understand what works, what doesn't, and what would make Clustr 
              indispensable for your daily work and pleasure.
            </p>
            <p>During this phase:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>We collect anonymous usage analytics to understand feature adoption</li>
              <li>We'll send monthly surveys to gather your qualitative feedback</li>
              <li>Some features may change or evolve based on what we learn</li>
              <li>Plus we always want to hear from you directly, so there is a "Feedback" button to make it easy.</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Our Promise to You</h2>
            <p>
              While Clustr is experimental, we take your data seriously:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>You maintain full ownership of your content</li>
              <li>You can export your entire collection at any time (via CSV download)</li>
              <li>We'll provide advance notice of any significant changes</li>
              <li>Your feedback directly influences development priorities</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Known Limitations</h2>
            <p>
              As an early version, Clustr has a few limitations we're actively working to address:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Web Clipper: Currently works on desktop browsers only, and only as a Chrome extension</li>
              <li>Content Types: Some media types (PDFs, audio) have limited preview capabilities</li>
              <li>Performance: Large collections (500+ items) may experience slower loading times</li>
              <li>Mobile Experience: Optimized for desktop, but with complete mobile functionality</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Temporary Workarounds</h2>
            <p>
              While we develop permanent solutions, here are some workarounds for common issues:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>If the interface freezes, refreshing the page usually resolves the issue</li>
              <li>For videos that don't preview properly, you can a screenshot as a custom thumbnail</li>
              <li>To organize large Collections, we recommend using Tags extensively until we improve filtering</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Reporting Issues & Suggestions</h2>
            <p>
              Your feedback is invaluable! To report bugs or share suggestions:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Use the "Feedback" button in the bottom right corner of any screen</li>
              <li>Include steps to reproduce any bugs you encounter</li>
              <li>For feature requests, describe both what you want and why it would be valuable</li>
              <li>Screenshots are extremely helpful when reporting visual or UI issues: please email to <a href="mailto:contact@ux-flo.com?subject=Feedback" className="text-clustr-blue underline">contact@ux-flo.com</a></li>
            </ul>
            <p className="mt-4">
              We review all feedback and prioritize fixes and features based on user impact.
            </p>
            <p className="mt-4 font-medium">
              Thank you for being part of this experiment. Together, we're building a better way to curate digital inspiration.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Releases;
